import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { apiUrl } from '../../config/api';
import DocumentTile from '../UI/DocumentTile';
import Modal from '../UI/Modal';
import { CREDIT_REPORT_ACCEPT, guideMailto } from './creditReportView';

// The guide and messages, from the API (config/creditReportGuide.json): the
// one copy the web, the app and the API use. null while loading or when it
// cannot be reached.
export const useCreditReportGuide = () => {
  const [guide, setGuide] = useState(null);
  useEffect(() => {
    let alive = true;
    axios.get(apiUrl('/api/credit-report-guide'))
      .then((res) => { if (alive) setGuide(res.data || null); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  return guide;
};

/** "How to get your free credit report (ITC / TransUnion)", in the app's modal. */
export const CreditReportGuideModal = ({ guide, isOpen, onClose }) => (
  <Modal isOpen={isOpen} onClose={onClose} title={guide?.title || 'How to get your free credit report'} size="lg">
    {!guide ? (
      <p className="text-sm text-gray-600 dark:text-slate-300">The guide could not be loaded. Please try again in a moment.</p>
    ) : (
      <div className="space-y-4 text-sm text-gray-700 dark:text-slate-300">
        <p>{guide.intro}</p>
        <ol className="list-decimal pl-5 space-y-1.5">
          {(guide.steps || []).map((step, i) => {
            // The email address in a step is a link that opens the email.
            const at = guide.email ? step.indexOf(guide.email) : -1;
            return (
              <li key={i}>
                {at < 0 ? step : (
                  <>
                    {step.slice(0, at)}
                    <a href={guideMailto(guide)} className="font-medium text-blue-600 dark:text-blue-300 underline break-all">{guide.email}</a>
                    {step.slice(at + guide.email.length)}
                  </>
                )}
              </li>
            );
          })}
        </ol>
        <div>
          <p className="font-semibold text-gray-900 dark:text-slate-100">{guide.in_person_title}</p>
          <p>{guide.in_person}</p>
        </div>
        <div>
          <p className="font-semibold text-gray-900 dark:text-slate-100">{guide.why_title}</p>
          <p>{guide.why}</p>
        </div>
      </div>
    )}
  </Modal>
);

/**
 * The credit report on the upload step: the shared document tile, the report
 * date next to it, one line of help, and "How do I get this?".
 *   file / onPick / onRemove   the picked file
 *   reportDate / onDate        the date on the report
 *   required                   a client applying (red when missing)
 */
const CreditReportField = ({ guide, file, onPick, onRemove, reportDate, onDate, required = true }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex flex-col gap-2">
      <DocumentTile
        type="Credit report"
        label={guide?.label || 'Credit report (ITC / TransUnion)'}
        hint="Required · PDF, JPG or PNG"
        file={file}
        accept={CREDIT_REPORT_ACCEPT}
        onPick={onPick}
        onRemove={onRemove}
        required={required}
      />
      <label className="block">
        <span className="block text-[11px] font-medium text-gray-700 dark:text-slate-300 mb-0.5">{guide?.date_label || 'Report date'} <span className="text-rose-600">*</span></span>
        <input type="date" value={reportDate} onChange={(e) => onDate(e.target.value)} className="w-full !text-xs" required aria-required="true" />
      </label>
      <p className="text-[11px] text-gray-500 dark:text-slate-400">
        {guide?.helper || 'Required by the Microlending Act and NAMFISA. Free once a year from TransUnion.'}{' '}
        <button type="button" onClick={() => setOpen(true)} className="font-medium text-blue-600 dark:text-blue-300 hover:underline">
          {guide?.link_text || 'How do I get this?'}
        </button>
      </p>
      <CreditReportGuideModal guide={guide} isOpen={open} onClose={() => setOpen(false)} />
    </div>
  );
};

export default CreditReportField;
